
import { DOCUMENT, ElementRef, Injectable, OnDestroy, Renderer2, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { DeviceDetectionService } from 'src/app/core/services/device-detection/device-detection.service';
import { PlatformService } from 'src/app/core/services/platform/platform.service';
import { StoreConfigurationRepositoryService } from 'src/app/core/services/store-configuration/store-configuration-repository.service';
import { ThemeConfigurationService } from 'src/app/core/services/theme-configuration/theme-configuration.service';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { ThemeConfiguration } from 'src/app/home/components/dynamic-home-page/dynamic-home-page.component';
import { HomeRepositoryService } from 'src/app/home/services/home-repository.service';

// Interface for home page styles structure
interface HomePageStyles {
  footerStyle?: Array<{ [key: string]: string }>;
  headerStyle?: Array<{ [key: string]: string }>;
  layoutStyles?: Array<{ [key: string]: string }>;
  carouselStyles?: Array<{ [key: string]: string }>;
  carouselListStyles?: Array<{ [key: string]: string }>;
  carouselLayoutStyles?: Array<{ [key: string]: string }>;
  carousalNavigationStyle?: Array<{ [key: string]: string }>;
  componentStyle?: Array<{ [key: string]: string }>;
  [key: string]: Array<{ [key: string]: string }> | undefined;
}


@Injectable({
  providedIn: 'root'
})
export class HomePageRenderService implements OnDestroy {
  private storeConfigurationRepositoryService = inject(StoreConfigurationRepositoryService);
  private themeConfigurationService = inject(ThemeConfigurationService);
  private themeService = inject(ThemeService);
  private homeRepositoryService = inject(HomeRepositoryService);
  private document = inject<Document>(DOCUMENT);
  private platformService = inject(PlatformService);
  private deviceDetectionService = inject(DeviceDetectionService);
  private router = inject(Router);

  // ===== SERVICE STATE MANAGEMENT =====
  private _isRendering = signal<boolean>(false);

  // Debug counter to track render calls
  private renderCallCount = 0;

  // Removed static flags - using smart investigation instead

  // ===== DATA SIGNALS =====
  private _previewTemplate = signal<Array<any>>([]);
  readonly previewTemplate = this._previewTemplate.asReadonly();

  // ===== HEADER DATA SIGNALS (for direct access) =====
  private _headerData = signal<any>(null);
  private _isRendered = signal<boolean>(false);

  // ===== FOOTER DATA SIGNALS (for direct access) =====
  private _footerData = signal<any>(null);

  // ===== COMPUTED SIGNALS =====
  readonly isRendering = this._isRendering.asReadonly();
  readonly isRendered = this._isRendered.asReadonly();
  readonly headerData = this._headerData.asReadonly();
  readonly footerData = this._footerData.asReadonly();

  readonly hasPreviewTemplate = computed(() => this._previewTemplate().length > 0);
  readonly previewTemplateContent = computed(() => this._previewTemplate());

  // ===== SERVICE HEALTH =====
  readonly canProceed = computed(() =>
    !this._isRendering()
  );

  // ===== LEGACY PROPERTIES (for backward compatibility) =====
  renderer!: Renderer2;
  elementRef!: ElementRef;
  private _themeConfiguration = signal<ThemeConfiguration | null>(null);
  isBrowser = false;
  private timeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.isBrowser = this.platformService.isBrowser();
  }

  // ===== SERVICE INITIALIZATION =====
  initializeService(): void {
    // Service is ready when injected
  }

  // ===== ENHANCED RENDER METHOD =====
  renderHomePage(renderer: Renderer2): boolean {
    this.renderCallCount++;
    // Basic protection against concurrent calls
    if (this._isRendering()) {
      return false;
    }

    this.renderer = renderer;
    this._isRendering.set(true);

    try {
      const homePageData = this.storeConfigurationRepositoryService.homePage();

      if (!homePageData) {
        this._isRendering.set(false);
        return false;
      }

      let templateData: any;
      try {
        templateData = JSON.parse(homePageData);
      } catch (error) {
        this._isRendering.set(false);
        return false;
      }

      // Store header and footer before filtering
      const headerData = templateData[0][0];
      const footerData = templateData[0][templateData[0].length - 1];

      // Filter the content based on device
      templateData = this.removeSectionBasedOnDevice(templateData);

      // Set theme configuration only (no global theme override)

      this.themeConfigurationService.updateThemeConfiguration(templateData[2]);
      this._themeConfiguration.set(templateData[2]);

      // Apply homepage theme as global default for all pages
      this.applyHomepageThemeAsGlobalDefault();

      // Repository service calls removed - components now access data directly via signals

      // Set signals for direct access (avoid duplicate parsing)
      this._headerData.set(headerData);
      this._footerData.set(footerData);
      this._isRendered.set(true);

      // Create the template data without header and footer for rendering
      const contentWithoutHeaderFooter = templateData[0].filter((item: any, index: number) =>
        index !== 0 && index !== templateData[0].length - 1
      );

      const tempLateWithoutHeaderData = [contentWithoutHeaderFooter, templateData[1]];

      // Update the signal
      this._previewTemplate.set(tempLateWithoutHeaderData);
      this.updateHomePageStyle(templateData[1]);

      // Apply theme styles when data is available
      this.applyThemeStyle();

      this._isRendering.set(false);

      return true;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error rendering home page';
      this._isRendering.set(false);

      return false;
    }
  }

  removeSectionBasedOnDevice(templateData: any): any {
    // Use device detection service for filtering
    const isMobile = this.deviceDetectionService.isMobile();

    // Get header and footer (first and last elements)
    const headerData = templateData[0][0];
    const footerData = templateData[0][templateData[0].length - 1];

    // Get content sections (everything except first and last)
    const contentSections = templateData[0].slice(1, -1);

    // Filter content based on device type
    const filteredContent = isMobile
      ? this.filterTemplateForMobile(contentSections)
      : this.filterTemplateForDesktop(contentSections);

    // Reconstruct the template data with header, filtered content, and footer
    templateData[0] = [headerData, ...filteredContent, footerData];

    return templateData;
  }

  private filterTemplateForMobile(templateArray: any[]): any[] {
    const wordToFind = 'hide-on-mobile';
    return templateArray.filter(a => {
      if (typeof a === 'string') {
        return !a.includes(wordToFind);
      }
      if (typeof a === 'object' && a !== null) {
        return !Object.values(a).some(value => typeof value === 'string' && value.includes(wordToFind));
      }
      return true;
    });
  }

  private filterTemplateForDesktop(templateArray: any[]): any[] {
    const wordToFind = 'hide-on-desktop';
    return templateArray.filter(a => {
      if (typeof a === 'string') {
        return !a.includes(wordToFind);
      }
      if (typeof a === 'object' && a !== null) {
        return !Object.values(a).some(value => typeof value === 'string' && value.includes(wordToFind));
      }
      return true;
    });
  }

  applyThemeStyle() {
    const themeConfig = this._themeConfiguration();

    if (!this.renderer) {
      return;
    }

    // Apply theme styles in both SSR and browser for proper rendering
    const themeContainers = this.document.getElementsByClassName("dynamic-theme-section");

    // Also check for any elements that might have similar classes
    const allElements = this.document.querySelectorAll('*');
    const elementsWithTheme = Array.from(allElements).filter(el =>
      el.className && el.className.includes('theme')
    );

    for (let i = 0; i < themeContainers.length; i++) {
      const themeContainer = themeContainers[i];

      if (themeConfig?.themeBackgroundColor) {
        this.renderer.setStyle(themeContainer, 'background-color', themeConfig.themeBackgroundColor);
      }

      if (themeConfig?.themeFontFamily) {
        this.renderer.setStyle(themeContainer, 'font-family', themeConfig.themeFontFamily);
      }
    }

    // If no dynamic-theme-section elements found, apply to body as fallback
    if (themeContainers.length === 0 && themeConfig?.themeFontFamily) {
      this.renderer.setStyle(this.document.body, 'font-family', themeConfig.themeFontFamily);
    }

    // Also apply to any elements with common theme classes
    const commonThemeClasses = ['theme-container', 'main-content', 'app-content', 'content-wrapper'];
    commonThemeClasses.forEach(className => {
      const elements = this.document.getElementsByClassName(className);
      if (elements.length > 0) {
        for (let i = 0; i < elements.length; i++) {
          if (themeConfig?.themeFontFamily) {
            this.renderer.setStyle(elements[i], 'font-family', themeConfig.themeFontFamily);
          }
          if (themeConfig?.themeBackgroundColor) {
            this.renderer.setStyle(elements[i], 'background-color', themeConfig.themeBackgroundColor);
          }
        }
      }
    });
  }

  updateHomePageStyle(homePageStyle: HomePageStyles) {
    // Check if we're on the home page
    const isHomePage = this.router.url === '/' || this.router.url === '';

    Object.entries(homePageStyle).forEach(([keyframes, value]) => {
      // For non-home pages, only apply header and footer styles
      if (!isHomePage) {
        if ((keyframes === 'headerStyle' || keyframes === 'footerStyle') && Array.isArray(value)) {
          this.updateStyle(value);
        }
      } else {
        // For home page, apply all styles as before
        if (Array.isArray(value)) {
          this.updateStyle(value);
        }
      }
    });
  }

  private appendStyle(sectionId: string, styles: string) {


    // Check if style already exists to prevent duplicates (works in both SSR and browser)
    if (this.document?.getElementById(sectionId)) {
      return;
    }

    const style = this.renderer.createElement('style');
    style.id = sectionId;
    const text = this.renderer.createText(styles || "");
    this.renderer.appendChild(style, text);
    const head = this.document.getElementsByTagName('head')[0];
    this.renderer.appendChild(head, style);
  }

  clearTimeout() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = setTimeout(() => { }, 0);
    }
  }

  updateStyle(styles: {}[]) {
    // Ensure carouselListStyles is an array and has elements
    if (Array.isArray(styles) && styles.length > 0) {
      styles.forEach((styleVal: {}, index: number) => {
        // Ensure styleVal is an object
        if (typeof styleVal === 'object' && styleVal !== null) {
          const entries = Object.entries(styleVal);

          // Ensure styleVal has at least one entry
          if (entries.length > 0) {
            const firstEntry = entries[0];
            if (firstEntry) {
              const [key, value] = firstEntry; // Extracting the key-value pair

              const originalKey = key;
              const modifiedKey = originalKey.replace("style-", "");

              setTimeout(() => {
                const sectionToAddClass = this.document.getElementById(modifiedKey);
                if (sectionToAddClass) {
                  sectionToAddClass.classList.add(key);
                }
              }, 500);

              // Ensure key is a string and value is defined
              if (typeof key === 'string' && typeof value !== 'undefined') {
                this.appendStyle(key, value as string);
              }
            }
          }
        }
      });
    }
  }

  // ===== UTILITY METHODS =====

  // Reset service state
  resetService(): void {
    this._isRendering.set(false);
  }


  // Check if service can safely be used
  canSafelyUse(): boolean {
    return this.canProceed();
  }

  // Get current service status
  getServiceStatus(): {
    isRendering: boolean;
  } {
    return {
      isRendering: this._isRendering()
    };
  }

  // Get current theme configuration (reactive)
  getThemeConfiguration(): ThemeConfiguration | null {
    return this._themeConfiguration() || null;
  }

  // Reactive signal for theme configuration
  readonly themeConfigurationSignal = computed(() => this._themeConfiguration());

  // Apply homepage theme as global default for all pages (only font)
  private applyHomepageThemeAsGlobalDefault() {
    const homepageTheme = this._themeConfiguration();
    if (!homepageTheme || !this.renderer) {
      return;
    }

    // Apply only font globally (keep other font settings as default)
    if (homepageTheme.themeFontFamily) {
      const defaultFontConfig = {
        fontFamily: homepageTheme.themeFontFamily,
        regularFontFileUrl: null,
        mediumFontFileUrl: null,
        boldFontFileUrl: null
      };

      // this.themeService.setFontVariables(defaultFontConfig, this.renderer);
    }
  }

  // Retry rendering
  async retryRender(renderer: Renderer2): Promise<boolean> {
    this.resetService();
    return this.renderHomePage(renderer);
  }

  // Public setter methods for signals
  setPreviewTemplate(template: Array<any>) {
    this._previewTemplate.set(template);
  }

  clearPreviewTemplate() {
    this._previewTemplate.set([]);
  }

  ngOnDestroy() {
    this.clearTimeout();
  }
}
